import { useState } from 'react'
import { Button, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ideon-api/ideon-ui'
import { featureLabel } from '../../lib/feature-label'
import universe from '../../lib/entitlement-universe.json'
import { BandHeader, Panel } from '../../components/Panel'

interface Feature {
  feature: string
  value?: unknown
}

// plan_library_limited restricts rather than grants; the Restrictions group and its tone carry that, not the value text.
// See docs/knowledge/known-issues-entitlements.md.
const RESTRICTION_FEATURES = ['plan_library_limited']

const enabledOf = (value: unknown) => (value as { enabled?: unknown } | null | undefined)?.enabled

type Tone = 'on' | 'restriction'

// One vocabulary in every group; a string value such as "enhanced" shows verbatim.
const displayOf = (enabled: unknown) => {
  if (enabled === true) return 'On'
  if (enabled === false) return 'Off'
  if (typeof enabled === 'string') return enabled
  return 'Not set'
}

const valueTone = (tone: Tone, enabled: unknown) => {
  if (tone === 'on') return 'text-ideon-magenta-70'
  if (enabled === true) return 'text-ideon-state-error-dark'
  return 'text-ideon-gray-70'
}

function FeatureRow({ feature, display, className }: { feature: string; display: string; className: string }) {
  const label = featureLabel(feature)
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4 gap-y-0.5 border-b border-ideon-gray-40 px-4 py-2.5 text-sm last:border-b-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
      <span className="text-ideon-gray-black max-sm:col-start-1 max-sm:row-start-1">{label !== feature && label}</span>
      <code className="break-all font-mono text-xs text-ideon-gray-70 max-sm:col-span-2 max-sm:row-start-2">{feature}</code>
      <span className={`font-mono text-xs max-sm:col-start-2 max-sm:row-start-1 ${className}`}>{display}</span>
    </li>
  )
}

function FeatureGroup({ name, tone, features }: { name: string; tone: Tone; features: Feature[] }) {
  if (features.length === 0) return null
  return (
    <div role="group" aria-label={name}>
      <BandHeader label={name.toUpperCase()} count={String(features.length)} />
      <ul>
        {features.map(({ feature, value }) => {
          const enabled = enabledOf(value)
          return (
            <FeatureRow
              key={feature}
              feature={feature}
              display={displayOf(enabled)}
              className={valueTone(tone, enabled)}
            />
          )
        })}
      </ul>
    </div>
  )
}

export function FeaturesCard({ features }: { features: Feature[] }) {
  const [notEnabledOpen, setNotEnabledOpen] = useState(false)
  const byKey = new Map(features.map((f) => [f.feature, f]))
  const active = features.filter((f) => !!enabledOf(f.value))
  const on = active.filter((f) => !RESTRICTION_FEATURES.includes(f.feature))
  const restrictions = active.filter((f) => RESTRICTION_FEATURES.includes(f.feature))
  // Every universe key not active, then any off key outside the universe.
  const keys = [...new Set([...universe.feature_flag_keys, ...features.map((f) => f.feature)])]
  const notEnabled = keys.filter((k) => !active.some((f) => f.feature === k))

  return (
    <Panel title="Features, entitlements and restrictions" subtitle="Not period-scoped">
      <FeatureGroup name="On" tone="on" features={on} />
      <FeatureGroup name="Restrictions" tone="restriction" features={restrictions} />
      <Collapsible role="group" aria-label="Not enabled" open={notEnabledOpen} onOpenChange={setNotEnabledOpen}>
        <BandHeader label="NOT ENABLED" count={String(notEnabled.length)}>
          <CollapsibleTrigger asChild>
            <Button type="button" size="sm" variant="outline" aria-label={`${notEnabledOpen ? 'Collapse' : 'Expand'} not enabled features`}>
              {notEnabledOpen ? 'Collapse' : 'Expand'}
            </Button>
          </CollapsibleTrigger>
        </BandHeader>
        <CollapsibleContent>
          <ul>
            {notEnabled.map((key) => {
              return <FeatureRow key={key} feature={key} display={displayOf(enabledOf(byKey.get(key)?.value))} className="text-ideon-gray-70" />
            })}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </Panel>
  )
}
